/**
 * Admin page (JF 2026-09-29), admins only:
 *  - Overview: how often files were uploaded and parsed, how often "Export Excel" was clicked and by how many people,
 *    per person and per day;
 *  - Parse records: each estimate with what the AI read (checklist), the questions and answers, the AI's pricing and the FOB;
 *  - Exports, everyone's saved cost sheets, and accounts (create, role, tools, disable; passwords only without Microsoft SSO).
 * Opened from the fabric price tool (tool="fabric", JF 2026-10-08) it shows that tool's usage and query records instead;
 * accounts are shared — each one is given AutoCosting and / or the fabric tool.
 */
import { Fragment, useEffect, useState } from "react";
import { DEFAULT_FX, DEFAULT_QCOEF, f2, n, type Items, type QCoef } from "@ic/engine";
import { useStore } from "../../store.ts";
import { okToReplace } from "../../shared/unsaved.ts";
import { useT } from "../../shared/i18n.ts";
import { localTime } from "../../shared/time.ts";
import { api, type AdminJobs, type BomRecord, type AdminSheet, type AdminUser, type ExportRow, type FabricRecord, type Range, type RunDetail, type RunRow, type Stats, type Tool } from "../../shared/api.ts";
import { Field, Modal } from "../../shared/ui/common.tsx";
import { COEF_FIELDS } from "../cost-sheet/coef.ts";
import { Checklist } from "../ai-estimate/Checklist.tsx";
import { Backtest } from "./Backtest.tsx";
import { Rules } from "./Rules.tsx";

type Tab = "overview" | "jobs" | "rules" | "backtest" | "runs" | "exports" | "bom" | "sheets" | "coef" | "fabric" | "users";
const when = (s: string | null | undefined) => localTime(s); // in the viewer's time zone (JF 2026-10-14)
const who = (x: { displayName?: string | null; username?: string | null }) => x.displayName || x.username || "—";
const usd = (v: unknown) => (v == null || v === "" ? "—" : "$" + f2(v));

export function Admin({ tool = "costing" }: { tool?: Tool }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>("overview");
  const [btDraft, setBtDraft] = useState(false); // "backtest with the draft" from the rules page
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [sso, setSso] = useState(true); // Microsoft login on → no password fields (JF 2026-10-09)
  const [range, setRange] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const reloadUsers = () => api.users().then((r) => { setUsers(r.items); setSso(r.sso); }).catch(() => undefined);
  const [exporting, setExporting] = useState(false);
  /** Every account's usage in the chosen period → Excel (JF 2026-10-09). */
  async function exportAll() {
    setExporting(true);
    try {
      const { exportUsersXlsx } = await import("./exportUsers.ts");
      const name = await exportUsersXlsx(await api.stats(range), range);
      useStore.getState().toast(t("Exported ", "已导出 ") + name);
    } catch (e) { useStore.getState().toast(e instanceof Error ? e.message : String(e)); } finally { setExporting(false); }
  }
  useEffect(() => { reloadUsers(); }, []);
  const tabs: [Tab, string][] = tool === "fabric"
    ? [["overview", t("Overview", "概览")], ["fabric", t("Fabric queries", "面料询价记录")], ["users", t("Accounts", "账户")]]
    : [["overview", t("Overview", "概览")], ["jobs", t("Live estimates", "实时任务")], ["rules", t("Pricing rules", "估价规则")], ["backtest", t("Backtest", "回测")], ["runs", t("Parse records", "解析记录")], ["exports", t("Excel exports", "导出记录")], ["bom", t("Historical BOM", "历史 BOM 记录")],
      ["sheets", t("Saved cost sheets", "保存的成本表")], ["coef", t("Quote coefficients", "报价系数")], ["users", t("Accounts", "账户")]];
  return (
    <div className="admin">
      <div className="panel">
        <div className="adm-head">
          <h2 style={{ margin: 0 }}>{t("Admin", "后台")}<span className="dim" style={{ fontSize: 13, fontWeight: 400 }}> · {tool === "fabric" ? t("Fabric price", "面料询价") : "Instant Costing"}</span></h2>
          <div className="seg" id="admTabs">{tabs.map(([k, l]) => <button key={k} data-tab={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>)}</div>
          {tab !== "users" && tab !== "sheets" && tab !== "coef" && (
            <div className="adm-range">
              <label>{t("From", "从")} <input type="date" className="in" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></label>
              <label>{t("to", "到")} <input type="date" className="in" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></label>
              {(range.from || range.to) && <button className="btn sm" onClick={() => setRange({ from: "", to: "" })}>{t("All time", "全部时间")}</button>}
              {tab === "overview" && <button className="btn sm p" id="exportUsers" disabled={exporting} onClick={exportAll}>{exporting ? t("Exporting…", "正在导出…") : t("Export Excel (all users)", "导出 Excel（全部用户）")}</button>}
            </div>
          )}
        </div>
      </div>
      {tab === "overview" && (tool === "fabric" ? <FabricOverview range={range} /> : <Overview range={range} />)}
      {tab === "fabric" && <FabricRecords range={range} users={users} />}
      {tab === "runs" && <Runs range={range} users={users} />}
      {tab === "exports" && <Exports range={range} users={users} />}
      {tab === "bom" && <BomUse range={range} users={users} />}
      {tab === "jobs" && <LiveJobs />}
      {tab === "rules" && <Rules onBacktest={() => { setBtDraft(true); setTab("backtest"); }} />}
      {tab === "backtest" && <Backtest draftFirst={btDraft} />}
      {tab === "sheets" && <Sheets users={users} />}
      {tab === "coef" && <CompanyCoef />}
      {tab === "users" && <Users users={users} reload={reloadUsers} defaultTools={["costing", "fabric"]} sso={sso} />}
    </div>
  );
}

function Overview({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const [s, setS] = useState<Stats | null>(null);
  useEffect(() => { api.stats(range).then(setS).catch((e) => useStore.getState().toast(String(e.message || e))); }, [range]);
  if (!s) return <div className="panel dim">{t("Loading…", "加载中…")}</div>;
  const max = Math.max(1, ...s.daily.map((d) => Math.max(d.parses, d.exports, d.bom || 0)));
  return (
    <>
      <div className="kpis" id="kpis">
        <div className="kpi"><b data-kpi="parses">{s.parses}</b><span>{t("uploads parsed", "上传文件开始解析（次）")}</span><small>{s.parsers} {t("people", "人")} · {s.priced} {t("priced", "出了成本表")} · {s.failed} {t("failed", "失败")}</small></div>
        <div className="kpi"><b data-kpi="exports">{s.exports}</b><span>{t("“Export Excel” clicks", "点击「导出 Excel」（次）")}</span><small>{s.exporters} {t("people", "人")}</small></div>
        {/* some people only use the history, never the AI (JF 2026-10-14) */}
        <LiveJobsKpi />
        <div className="kpi"><b data-kpi="bomApplies">{s.bomApplies}</b><span>{t("historical BOMs applied", "套用历史 BOM（次）")}</span><small>{s.bomViews} {t("styles opened", "次打开历史款")} · {s.bomUsers} {t("people", "人")}</small></div>
      </div>
      <div className="panel">
        <h3>{t("Per person", "按人统计")}</h3>
        <table className="t" id="perUser"><tbody>
          <tr><th>{t("Account", "账户")}</th><th className="n">{t("Parses", "解析")}</th><th className="n">{t("Priced", "出表")}</th><th className="n">{t("Exports", "导出")}</th><th className="n">{t("History opened", "打开历史款")}</th><th className="n">{t("BOM applied", "套用历史 BOM")}</th><th className="n">{t("Saved sheets", "保存的表")}</th><th>{t("Last active", "最近活动")}</th><th>{t("Last login", "最近登录")}</th></tr>
          {s.users.map((u) => (
            <tr key={u.id} style={u.active ? undefined : { opacity: 0.5 }}>
              <td>{who(u)} <span className="dim mono">{u.username}</span>{u.role === "admin" && <span className="chip" style={{ marginLeft: 6 }}>admin</span>}</td>
              <td className="n">{u.parses}</td><td className="n">{u.priced}</td><td className="n">{u.exports}</td><td className="n">{u.bomViews}</td><td className="n">{u.bomApplies}</td><td className="n">{u.sheets}</td>
              <td>{when(u.lastActive)}</td><td>{when(u.lastLoginAt)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <div className="panel">
        <h3>{t("Per day", "按天")}</h3>
        {!s.daily.length && <div className="dim">{t("No activity in this period.", "这段时间没有活动。")}</div>}
        <table className="t daily"><tbody>
          {s.daily.map((d) => (
            <tr key={d.d}><td className="mono">{d.d}</td>
              <td style={{ width: "28%" }}><div className="bar p" style={{ width: (d.parses / max) * 100 + "%" }} /><span className="dim">{d.parses} {t("parses", "次解析")}</span></td>
              <td style={{ width: "28%" }}><div className="bar e" style={{ width: (d.exports / max) * 100 + "%" }} /><span className="dim">{d.exports} {t("exports", "次导出")}</span></td>
              <td style={{ width: "28%" }}><div className="bar b" style={{ width: ((d.bom || 0) / max) * 100 + "%" }} /><span className="dim">{d.bom || 0} {t("BOMs applied", "次套用历史 BOM")}</span></td></tr>
          ))}
        </tbody></table>
      </div>
    </>
  );
}

function Filters({ users, user, setUser, q, setQ, placeholder }: { users: AdminUser[]; user: string; setUser(v: string): void; q: string; setQ(v: string): void; placeholder: string }) {
  const t = useT();
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
      <select className="in" style={{ maxWidth: 220 }} value={user} onChange={(e) => setUser(e.target.value)}>
        <option value="">{t("Everyone", "所有人")}</option>{users.map((u) => <option key={u.id} value={u.id}>{who(u)}</option>)}</select>
      <input className="in" style={{ maxWidth: 320 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
const PAGE = 50;
function Pager({ total, offset, setOffset }: { total: number; offset: number; setOffset(n: number): void }) {
  const t = useT();
  if (total <= PAGE) return <div className="dim" style={{ fontSize: 12 }}>{total} {t("records", "条")}</div>;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
      <button className="btn sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>‹</button>
      <span className="dim">{offset + 1}–{Math.min(total, offset + PAGE)} / {total}</span>
      <button className="btn sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>›</button>
    </div>
  );
}
function useList<T>(fetcher: (r: Range) => Promise<{ total: number; items: T[] }>, deps: { from?: string; to?: string }) {
  const [user, setUser] = useState(""), [q, setQ] = useState(""), [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ total: number; items: T[] } | null>(null);
  useEffect(() => { setOffset(0); }, [user, q, deps.from, deps.to]);
  useEffect(() => {
    const tm = setTimeout(() => fetcher({ ...deps, user, q, offset, limit: PAGE }).then(setData).catch((e) => useStore.getState().toast(String(e.message || e))), 200);
    return () => clearTimeout(tm);
  }, [user, q, offset, deps.from, deps.to]); // eslint-disable-line react-hooks/exhaustive-deps
  return { user, setUser, q, setQ, offset, setOffset, data };
}

const STATUS: Record<string, [string, string]> = { started: ["started", "开始"], extracted: ["read", "已识别"], priced: ["priced", "已出表"], failed: ["failed", "失败"] };

function Runs({ range, users }: { range: { from: string; to: string }; users: AdminUser[] }) {
  const t = useT();
  const L = useList<RunRow>(api.runs, range);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="panel">
      <Filters users={users} user={L.user} setUser={L.setUser} q={L.q} setQ={L.setQ} placeholder={t("Customer / style no. / file name", "客户 / 款号 / 文件名")} />
      <table className="t" id="runList"><tbody>
        <tr><th>{t("Time", "时间")}</th><th>{t("Who", "谁")}</th><th>{t("Files", "文件")}</th><th>{t("Customer", "客户")}</th><th>{t("Style no.", "款号")}</th><th>{t("Status", "状态")}</th><th className="n">{t("Quoted FOB", "报价 FOB")}</th></tr>
        {L.data?.items.map((r) => (
          <tr className="rec" key={r.id} data-run={r.id} onClick={() => setOpen(r.id)}>
            <td className="mono">{when(r.createdAt)}</td><td>{who(r)}</td>
            <td>{r.files.map((f) => f.name).join(", ") || <span className="dim">{t("notes only", "只有说明")}</span>}</td>
            <td>{r.customer}</td><td className="mono">{r.styleNo}</td>
            <td><span className={"chip" + (r.status === "priced" ? " ok" : "")} title={r.error || ""}>{t(...(STATUS[r.status] || [r.status, r.status]))}</span>{r.pricings > 1 && <span className="dim"> ×{r.pricings}</span>}{r.mode === "direct" && <span className="dim"> · {t("direct", "直连")}</span>}</td>
            <td className="n">{usd(r.totals?.qfob)}</td>
          </tr>
        ))}
      </tbody></table>
      {L.data && !L.data.items.length && <div className="dim">{t("No parse records yet.", "还没有解析记录。")}</div>}
      {L.data && <Pager total={L.data.total} offset={L.offset} setOffset={L.setOffset} />}
      {open && <RunModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function RunModal({ id, onClose }: { id: string; onClose(): void }) {
  const t = useT();
  const [r, setR] = useState<RunDetail | null>(null);
  const [raw, setRaw] = useState(false);
  useEffect(() => { api.run(id).then(setR).catch((e) => useStore.getState().toast(String(e.message || e))); }, [id]);
  const items = (r?.finalItems || r?.items) as Items | null;
  const p = r?.price as (Record<string, unknown> & { fabrics?: Record<string, unknown>[]; trims?: Record<string, unknown>[] }) | null;
  return (
    <Modal onClose={onClose} wide>
      {!r ? <div className="dim">{t("Loading…", "加载中…")}</div> : (
        <div id="runDetail">
          <h3>{r.customer || "—"} · {r.styleNo || "—"} <span className="dim" style={{ fontWeight: 400, fontSize: 13 }}>{who(r)} · {when(r.createdAt)} · {t(...(STATUS[r.status] || [r.status, r.status]))}</span></h3>
          {r.error && <div className="warn">{r.error}</div>}
          <div className="kv">
            <div>{t("Files", "文件")}</div><div>{r.files.map((f) => `${f.name} (${Math.max(1, Math.round(f.size / 1024))} KB)`).join(" · ") || "—"}</div>
            <div>{t("Notes", "补充说明")}</div><div style={{ whiteSpace: "pre-wrap" }}>{r.notes || "—"}</div>
            <div>{t("Style", "款式")}</div><div>{r.style ? Object.entries(r.style).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ") : "—"}</div>
            {r.totals && <><div>FOB</div><div>{t("actual", "成本")} {usd(r.totals.fob)} · {t("quoted", "报价")} {usd(r.totals.qfob)} · CM {usd(r.totals.cm)} → {usd(r.totals.qcm)} · GP {r.totals.gpp != null ? (r.totals.gpp * 100).toFixed(1) + "%" : "—"}</div></>}
            <div>{t("Exported", "导出")}</div><div>{r.exports.length ? r.exports.map((x) => when(x.at)).join(" · ") : "—"}</div>
          </div>
          <div className="adm-cols">
            <div>
              <h4>{t("Checklist the AI read", "AI 识别的清单")}{r.finalItems && <span className="dim"> · {t("after answers / corrections", "含回答和修改")}</span>}</h4>
              <Checklist items={items} />
            </div>
            <div>
              <h4>{t("Questions & answers", "提问和回答")}</h4>
              {!r.questions?.length ? <div className="dim">{t("No questions.", "没有提问。")}</div> : r.questions.map((q) => {
                const a = r.answers?.[q.key];
                return <div key={q.key} className="qa"><div>{q.q_zh || q.q_en}</div><div className="dim">→ {a ? a.label || a.v : t("don't know / not answered", "不知道 / 没回答")}</div></div>;
              })}
            </div>
          </div>
          <h4>{t("AI pricing", "AI 定价结果")}</h4>
          {!p ? <div className="dim">{t("Not priced.", "还没定价。")}</div> : (
            <>
              <table className="t sm"><tbody>
                <tr><th>{t("Fabric", "面料")}</th><th>{t("Position", "部位")}</th><th>{t("Supplier", "供应商")}</th><th>{t("Category", "类别")}</th><th className="n">{t("Price", "单价")}</th><th className="n">{t("Yield", "用量")}</th><th>{t("Basis", "依据")}</th></tr>
                {(p.fabrics || []).map((x, i) => <tr key={i}><td>{String(x.name ?? "")}</td><td>{String(x.position ?? "")}</td><td>{String(x.supplier ?? "")}</td><td>{String(x.fab_cat ?? "")}</td><td className="n">{usd(x.px)}</td><td className="n">{String(x.yield ?? "")} {String(x.unit ?? "")}</td><td className="dim">{String(x.basis ?? "")}</td></tr>)}
              </tbody></table>
              <table className="t sm" style={{ marginTop: 8 }}><tbody>
                <tr><th>{t("Trim", "辅料")}</th><th>{t("Position", "部位")}</th><th>{t("Supplier", "供应商")}</th><th>{t("Category", "类别")}</th><th className="n">{t("Price", "单价")}</th><th className="n">{t("Qty", "用量")}</th><th>{t("Basis", "依据")}</th></tr>
                {(p.trims || []).map((x, i) => <tr key={i}><td>{String(x.name ?? "")}</td><td>{String(x.position ?? "")}</td><td>{String(x.supplier ?? "")}</td><td>{String(x.trim_cat ?? "")}</td><td className="n">{usd(x.px)}</td><td className="n">{String(x.yield ?? "")}</td><td className="dim">{String(x.basis ?? "")}</td></tr>)}
              </tbody></table>
              <div className="kv" style={{ marginTop: 8 }}>
                <div>CM</div><div>{usd(p.cm_base)}{p.rush_pct ? ` + ${String(p.rush_pct)}% ${t("rush", "加急")}` : ""} · SAM {String(p.sam ?? "—")} · {String(p.city ?? "")}</div>
                <div>{t("CM reason", "CM 理由")}</div><div>{String(p.cm_reason_zh || p.cm_reason_en || "—")}</div>
                <div>{t("Testing / inspection", "检测 / 验货")}</div><div>{usd(p.testing_total)} / {usd(p.inspection_total)}</div>
                <div>{t("Reference SCs", "参考历史款")}</div><div className="mono">{Array.isArray(p.ref_scs) ? p.ref_scs.join(", ") : "—"}</div>
              </div>
            </>
          )}
          <div style={{ marginTop: 10 }}><span className="lnk" onClick={() => setRaw(!raw)}>{raw ? t("Hide raw data", "收起原始数据") : t("Show raw data (JSON)", "查看原始数据（JSON）")}</span></div>
          {raw && <pre className="raw">{JSON.stringify({ style: r.style, items: r.items, questions: r.questions, answers: r.answers, finalItems: r.finalItems, price: r.price, totals: r.totals }, null, 2)}</pre>}
        </div>
      )}
      <div className="mb"><button className="btn" onClick={onClose}>{t("Close", "关闭")}</button></div>
    </Modal>
  );
}

function Exports({ range, users }: { range: { from: string; to: string }; users: AdminUser[] }) {
  const t = useT();
  const L = useList<ExportRow>(api.exports, range);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="panel">
      <Filters users={users} user={L.user} setUser={L.setUser} q={L.q} setQ={L.setQ} placeholder={t("Customer / style no.", "客户 / 款号")} />
      <table className="t" id="exportList"><tbody>
        <tr><th>{t("Time", "时间")}</th><th>{t("Who", "谁")}</th><th>{t("Customer", "客户")}</th><th>{t("Style no.", "款号")}</th><th>{t("Name", "款名")}</th><th className="n">{t("Quoted FOB", "报价 FOB")}</th><th></th></tr>
        {L.data?.items.map((x) => (
          <tr key={x.id}><td className="mono">{when(x.at)}</td><td>{who(x)}</td><td>{x.customer}</td><td className="mono">{x.styleNo}</td><td>{String(x.detail.name ?? "")}</td>
            <td className="n">{usd(x.detail.qfob)}</td>
            <td>{x.runId && <span className="lnk" onClick={() => setOpen(x.runId)}>{t("parse record", "解析记录")}</span>}</td></tr>
        ))}
      </tbody></table>
      {L.data && !L.data.items.length && <div className="dim">{t("No exports yet.", "还没有导出记录。")}</div>}
      {L.data && <Pager total={L.data.total} offset={L.offset} setOffset={L.setOffset} />}
      {open && <RunModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Historical BOM: who opened / applied which style (JF 2026-10-14). */
function BomUse({ range, users }: { range: { from: string; to: string }; users: AdminUser[] }) {
  const [kind, setKind] = useState<"" | "bom_view" | "bom_apply">("bom_apply");
  return <BomList key={kind} kind={kind} setKind={setKind} range={range} users={users} />;
}
function BomList({ kind, setKind, range, users }: { kind: "" | "bom_view" | "bom_apply"; setKind(k: "" | "bom_view" | "bom_apply"): void; range: { from: string; to: string }; users: AdminUser[] }) {
  const t = useT();
  const L = useList<BomRecord>((r) => api.adminBom({ ...r, kind }), range);
  return (
    <div className="panel">
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span className="seg" id="bomKind">{([["bom_apply", t("applied", "套用")], ["bom_view", t("opened", "打开")], ["", t("both", "全部")]] as const).map(([k, l]) => (
          <button key={k} className={kind === k ? "on" : ""} data-bomkind={k} onClick={() => setKind(k)}>{l}</button>))}</span>
        <div style={{ flex: 1 }}><Filters users={users} user={L.user} setUser={L.setUser} q={L.q} setQ={L.setQ} placeholder={t("Customer / style no. / SC", "客户 / 款号 / SC")} /></div>
      </div>
      <table className="t" id="bomList"><tbody>
        <tr><th>{t("Time", "时间")}</th><th>{t("Who", "谁")}</th><th>{t("What", "动作")}</th><th>SC</th><th>{t("Customer", "客户")}</th><th>{t("Style no.", "款号")}</th><th>{t("Style", "款")}</th><th>{t("How", "方式")}</th></tr>
        {L.data?.items.map((x) => (
          <tr key={x.id}><td className="mono">{when(x.at)}</td><td>{who(x)}</td><td>{x.kind === "bom_apply" ? t("applied", "套用") : t("opened", "打开")}</td>
            <td className="mono">{x.detail.sc || ""}</td><td>{x.customer}</td><td className="mono">{x.styleNo}</td><td>{String(x.detail.name ?? "").slice(0, 60)}</td>
            <td className="dim">{x.kind === "bom_apply" ? (x.detail.rows ? t(`${x.detail.rows} ticked rows`, `勾选 ${x.detail.rows} 行`) : x.detail.mode === "add" ? t("added", "追加") : t("replaced", "替换")) : ""}</td></tr>
        ))}
      </tbody></table>
      {L.data && !L.data.items.length && <div className="dim">{t("Nothing in this period.", "这段时间没有记录。")}</div>}
      {L.data && <Pager total={L.data.total} offset={L.offset} setOffset={L.setOffset} />}
    </div>
  );
}

/** Estimates the server runs right now, for everyone (JF 2026-10-16): a card on the overview. */
function LiveJobsKpi() {
  const t = useT();
  const [j, setJ] = useState<AdminJobs | null>(null);
  useEffect(() => { const f = () => api.adminJobs().then(setJ).catch(() => undefined); f(); const h = window.setInterval(f, 5000); return () => window.clearInterval(h); }, []);
  if (!j) return null;
  return <div className="kpi"><b data-kpi="jobsRunning">{j.running}</b><span>{t("estimates running now (all users)", "正在并行估价（全站，款）")}</span>
    <small>{t(`${j.queued} queued · ${j.answer} waiting for answers · ${j.users} people`, `排队 ${j.queued} · 待回答 ${j.answer} · ${j.users} 人`)}</small></div>;
}

/** Every estimate on the server: who, which style, which step, how long (JF 2026-10-16). Admins can stop one and set the limits. */
function LiveJobs() {
  const t = useT();
  const [all, setAll] = useState(false);
  const [j, setJ] = useState<AdminJobs | null>(null);
  const [lim, setLim] = useState<{ perUser: string; total: string } | null>(null);
  const load = () => api.adminJobs(all ? "all" : "active").then((x) => { setJ(x); setLim((l) => l ?? { perUser: String(x.limits.perUser), total: String(x.limits.total) }); }).catch(() => undefined);
  useEffect(() => { load(); const h = window.setInterval(load, 3000); return () => window.clearInterval(h); }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!j) return <div className="panel dim">…</div>;
  const label: Record<string, string> = { queued: t("queued", "排队中"), extract: t("reading", "读取中"), answer: t("waiting for the AM", "等用户回答"), price: t("pricing", "定价中"),
    save: t("saving", "保存中"), done: t("done", "已完成"), err: t("failed", "失败"), cancelled: t("stopped", "已停止") };
  const since = (iso: string | null) => { if (!iso) return ""; const s = Math.round((Date.now() - Date.parse(iso)) / 1000); return s < 90 ? t(`${s} s`, `${s} 秒`) : t(`${Math.round(s / 60)} min`, `${Math.round(s / 60)} 分钟`); };
  async function saveLim() {
    try { await api.adminJobLimits({ perUser: Number(lim!.perUser), total: Number(lim!.total) }); useStore.getState().toast(t("Saved", "已保存")); load(); }
    catch (e) { useStore.getState().toast(e instanceof Error ? e.message : String(e)); }
  }
  return (
    <div className="panel" id="liveJobs">
      <div className="kpis">
        <div className="kpi"><b data-live="running">{j.running}</b><span>{t("running now", "正在跑（款）")}</span></div>
        <div className="kpi"><b data-live="queued">{j.queued}</b><span>{t("queued", "排队中")}</span></div>
        <div className="kpi"><b data-live="answer">{j.answer}</b><span>{t("waiting for answers", "等用户回答")}</span></div>
        <div className="kpi"><b data-live="users">{j.users}</b><span>{t("people with estimates running", "正在用的人")}</span></div>
        <div className="kpi"><b>{(j.last24h.done || 0) + "/" + ((j.last24h.done || 0) + (j.last24h.err || 0))}</b><span>{t("done / ended, last 24 h", "最近 24 小时完成 / 结束")}</span><small>{t(`${j.last24h.err || 0} failed · ${j.last24h.cancelled || 0} stopped`, `失败 ${j.last24h.err || 0} · 停止 ${j.last24h.cancelled || 0}`)}</small></div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "10px 0" }}>
        <span className="seg"><button className={!all ? "on" : ""} onClick={() => setAll(false)}>{t("running / waiting", "进行中 / 等待")}</button><button className={all ? "on" : ""} onClick={() => setAll(true)}>{t("all (latest 200)", "全部（最近 200）")}</button></span>
        {lim && <span className="dim" style={{ fontSize: 12.5, marginLeft: "auto" }}>{t("At most", "最多同时")} <input className="in" id="limUser" style={{ width: 52 }} value={lim.perUser} onChange={(e) => setLim({ ...lim, perUser: e.target.value })} /> {t("per person,", "款 / 人，全站")}
          {" "}<input className="in" id="limTotal" style={{ width: 52 }} value={lim.total} onChange={(e) => setLim({ ...lim, total: e.target.value })} /> {t("in all", "款")} <button className="btn sm" id="limSave" onClick={saveLim}>{t("Save", "保存")}</button></span>}
      </div>
      <table className="t" id="jobList"><tbody>
        <tr><th>{t("Started", "开始")}</th><th>{t("Who", "谁")}</th><th>{t("Style", "款")}</th><th>{t("Kind", "方式")}</th><th>{t("Step", "进度")}</th><th>{t("For", "已用")}</th><th></th></tr>
        {j.items.map((x) => (
          <tr key={x.id} data-job={x.id}>
            <td className="mono">{localTime(x.startedAt || x.createdAt)}</td><td>{x.displayName || x.username}</td>
            <td style={{ maxWidth: 300 }}>{x.title || x.customer}{x.status === "err" && x.error && <div className="dim" style={{ fontSize: 11.5 }}>{x.error.slice(0, 200)}</div>}</td>
            <td className="dim">{x.kind === "batch" ? t("batch", "批量") : t("single", "单款")}</td>
            <td>{["queued", "extract", "price", "save"].includes(x.status) && <i className="spin" />}{label[x.status]}</td>
            <td className="dim">{["done", "err", "cancelled"].includes(x.status) ? "" : since(x.startedAt || x.createdAt)}</td>
            <td>{["queued", "extract", "price", "save", "answer"].includes(x.status) && <button className="btn sm" onClick={async () => { if (confirm(t("Stop this estimate?", "停止这一款？"))) { await api.jobCancel(x.id).catch(() => undefined); load(); } }}>{t("Stop", "停止")}</button>}</td>
          </tr>))}
      </tbody></table>
      {!j.items.length && <div className="dim">{t("Nothing running.", "现在没有在跑的估价。")}</div>}
    </div>
  );
}

function Sheets({ users }: { users: AdminUser[] }) {
  const t = useT();
  const L = useList<AdminSheet>(api.allSheets, {});
  const { setSheet, go, toast } = useStore.getState();
  async function open(x: AdminSheet) {
    if (!okToReplace()) return; // the sheet being edited has unsaved changes (JF 2026-10-13)
    const r = await api.sheet(x.id);
    // someone else's sheet opens as a copy: saving it stores it under your account, theirs stays unchanged
    setSheet(r.data, { id: r.mine ? r.id : null }); useStore.getState().markSaved(r.data); go("sheet");
    if (!r.mine) toast(t(`Opened ${who(x)}'s cost sheet as a copy — theirs stays unchanged.`, `已打开 ${who(x)} 的成本表（副本，不会改到对方的）。`));
  }
  return (
    <div className="panel">
      <Filters users={users} user={L.user} setUser={L.setUser} q={L.q} setQ={L.setQ} placeholder={t("Style no. / customer / name", "款号 / 客户 / 款名")} />
      <table className="t" id="allSheets"><tbody>
        <tr><th>{t("Owner", "所有人")}</th><th>{t("Style no.", "款号")}</th><th>{t("Customer", "客户")}</th><th>{t("Name", "款名")}</th><th>{t("Saved", "保存时间")}</th></tr>
        {L.data?.items.map((x) => (
          <tr className="rec" key={x.id} onClick={() => open(x)}><td>{x.username ? who(x) : <span className="dim">{t("(before accounts)", "（账户上线前）")}</span>}</td>
            <td className="mono">{x.styleNo || "—"}</td><td>{x.customer}</td><td>{x.title}</td><td>{when(x.updatedAt)}</td></tr>
        ))}
      </tbody></table>
      {L.data && !L.data.items.length && <div className="dim">{t("No saved cost sheets yet.", "还没有保存的成本表。")}</div>}
      {L.data && <Pager total={L.data.total} offset={L.offset} setOffset={L.setOffset} />}
    </div>
  );
}

/**
 * Company default quote coefficients (JF 2026-10-13): a base set and one per order quantity. New sheets use, for a quantity,
 * the person's own set for it → the company's for it → their own base → the company base → the cost-sheet template.
 */
function CompanyCoef() {
  const t = useT();
  const toast = useStore.getState().toast;
  const [base, setBase] = useState<QCoef>({ ...DEFAULT_QCOEF });
  const [rows, setRows] = useState<{ qty: number; c: QCoef }[]>([]);
  const [newQty, setNewQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [fx, setFx] = useState(String(DEFAULT_FX)); // company default rate, RMB per USD (JF 2026-10-13)
  useEffect(() => {
    api.coefPrefs().then((r) => {
      if (r.fx) setFx(String(r.fx));
      const co = r.company;
      setBase({ ...DEFAULT_QCOEF, ...(co?.base || {}) });
      setRows(Object.entries(co?.byQty || {}).map(([q, c]) => ({ qty: Number(q), c: { ...DEFAULT_QCOEF, ...c } })).sort((a, b) => a.qty - b.qty));
      setLoaded(true);
    }).catch((e) => useStore.getState().toast(String(e.message || e)));
  }, []);
  const show = (v: number, kind: "pct" | "x") => (kind === "pct" ? String(+(v * 100).toFixed(4)) : String(v));
  const read = (v: string, kind: "pct" | "x") => (kind === "pct" ? n(v) / 100 : n(v));
  function addQty() {
    const q = Math.round(n(newQty));
    if (!(q > 0)) return;
    if (rows.some((r) => r.qty === q)) { toast(t(`${q} pcs is already in the list`, `${q} 件已经在列表里`)); return; }
    setRows([...rows, { qty: q, c: { ...base } }].sort((a, b) => a.qty - b.qty)); setNewQty("");
  }
  async function save() {
    setBusy(true);
    try {
      const p = { base, byQty: Object.fromEntries(rows.map((r) => [String(r.qty), r.c])) };
      await api.saveCompanyCoef(p);
      if (n(fx) > 0) { await api.saveCompanyFx(n(fx)); useStore.getState().setDefaultFx(n(fx)); }
      const st = useStore.getState();
      st.setCoefPrefs({ mine: st.coefPrefs?.mine ?? null, company: p });
      toast(t("Company defaults saved", "公司默认系数已保存"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  const cells = (c: QCoef, on: (c: QCoef) => void, key: string) => COEF_FIELDS.map(([k, , , kind]) => (
    <td key={k}><Field className="in n" dataK={`cc-${key}-${k}`} value={show(c[k] as number, kind)} onChange={(v) => on({ ...c, [k]: read(v, kind) })} /></td>
  ));
  if (!loaded) return <div className="panel dim">{t("Loading…", "加载中…")}</div>;
  return (
    <div className="panel" id="companyCoef">
      <p className="dim" style={{ marginTop: 0 }}>{t(
        "Defaults for new sheets. For an order quantity the sheet uses: the person's own default for it → the company's for it → their own base → the company base below. A quantity not listed uses the largest listed quantity below it (else the smallest).",
        "新建成本表时的默认报价系数。某个数量用：本人该档默认 → 公司该档默认 → 本人基础默认 → 下面的公司基础默认。没列出的数量，用不超过它的最大一档（都比它大就用最小一档）。")}</p>
      <div className="fl" style={{ maxWidth: 380, marginBottom: 12 }}><label>{t("Default exchange rate for new cost sheets", "新成本表的默认汇率")}</label>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>1 USD = <input className="in n" id="ccFx" style={{ maxWidth: 90 }} value={fx} onChange={(e) => setFx(e.target.value)} /> RMB
          <span className="dim" style={{ fontSize: 12 }}>{t("each sheet keeps its own rate; it can be changed on the sheet", "每张单保存自己的汇率，单上可以改")}</span></div></div>
      <table className="t" id="ccTable"><tbody>
        <tr><th>{t("Order qty", "数量档")}</th>{COEF_FIELDS.map(([k, en, zh, kind]) => <th key={k}>{t(en, zh)}{kind === "pct" ? " %" : ""}</th>)}<th /></tr>
        <tr data-ccrow="base"><td><b>{t("Base (any qty)", "基础（不分数量）")}</b></td>{cells(base, setBase, "base")}<td /></tr>
        {rows.map((r, i) => (
          <tr key={r.qty} data-ccrow={r.qty}><td className="mono">{r.qty} {t("pcs", "件")}</td>
            {cells(r.c, (c) => setRows(rows.map((x, j) => (j === i ? { ...x, c } : x))), String(r.qty))}
            <td><button className="btn sm" data-ccdel={r.qty} onClick={() => setRows(rows.filter((_, j) => j !== i))}>{t("Remove", "删除")}</button></td></tr>
        ))}
      </tbody></table>
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input className="in" id="ccNewQty" style={{ maxWidth: 140 }} value={newQty} onChange={(e) => setNewQty(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addQty(); }} placeholder={t("e.g. 300", "如 300")} />
        <button className="btn sm" id="ccAdd" onClick={addQty}>{t("+ Add quantity", "+ 加数量档")}</button>
        <button className="btn sm" onClick={() => setBase({ ...DEFAULT_QCOEF })}>{t("Base = template values", "基础恢复模板值")}</button>
        <button className="btn sm p" id="ccSave" style={{ marginLeft: "auto" }} disabled={busy} onClick={save}>{busy ? t("Saving…", "保存中…") : t("Save", "保存")}</button>
      </div>
    </div>
  );
}

const TOOL_LABEL: Record<Tool, [string, string]> = { costing: ["Instant Costing", "AutoCosting"], fabric: ["Fabric price", "面料询价"] };
/** Tick boxes for the tools an account may open (admins get everything). */
function ToolBoxes({ value, onChange, disabled, name }: { value: Tool[]; onChange(v: Tool[]): void; disabled?: boolean; name: string }) {
  const t = useT();
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>{(["costing", "fabric"] as Tool[]).map((k) => (
      <label key={k} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
        <input type="checkbox" data-tool={name + ":" + k} disabled={disabled} checked={value.includes(k)}
          onChange={(e) => onChange(e.target.checked ? [...new Set([...value, k])] : value.filter((x) => x !== k))} />{t(...TOOL_LABEL[k])}</label>
    ))}</div>
  );
}

function Users({ users, reload, defaultTools, sso }: { users: AdminUser[]; reload(): void; defaultTools: Tool[]; sso: boolean }) {
  const t = useT();
  const me = useStore((s) => s.user);
  const toast = useStore.getState().toast;
  const blank = { username: "", email: "", displayName: "", password: "", role: "user" as "admin" | "user", tools: defaultTools };
  const [nu, setNu] = useState(blank);
  const [edit, setEdit] = useState<string | null>(null);
  const [ed, setEd] = useState({ displayName: "", email: "", password: "", role: "user" as "admin" | "user", active: true, tools: ["costing"] as Tool[] });
  // newest first (from the server) and a search box — there will be hundreds of accounts (JF 2026-10-09)
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = needle ? users.filter((u) => [u.username, u.email || "", u.displayName].some((v) => v.toLowerCase().includes(needle))) : users;
  const err = (e: unknown) => toast(e instanceof Error ? e.message : String(e));
  async function create() {
    try { await api.createUser({ ...nu, username: nu.username.trim(), email: nu.email.trim() }); toast(t("Account created", "账户已创建")); setNu(blank); reload(); } catch (e) { err(e); }
  }
  async function remove(u: AdminUser) {
    if (!confirm(t(`Delete the account "${u.displayName || u.username}"? This cannot be undone. Their saved cost sheets and records are kept.`,
      `删除账户「${u.displayName || u.username}」？删除后无法恢复。他保存的成本表和使用记录会保留。`))) return;
    try { await api.deleteUser(u.id); toast(t("Account deleted", "账户已删除")); setEdit(null); reload(); } catch (e) { err(e); }
  }
  async function save(u: AdminUser) {
    try {
      await api.patchUser(u.id, { displayName: ed.displayName, email: ed.email.trim(), role: ed.role, active: ed.active, ...(ed.role === "user" ? { tools: ed.tools } : {}), ...(ed.password ? { password: ed.password } : {}) });
      toast(ed.password ? t("Saved — the new password works from now on", "已保存——新密码立即生效") : t("Saved", "已保存"));
      setEdit(null); reload();
    } catch (e) { err(e); }
  }
  return (
    <>
      <div className="panel" id="newUser">
        <h3>{t("New account", "新建账户")}</h3>
        <div className="grid4">
          <div className="fl"><label>{t("Company email (Microsoft login)", "公司邮箱（Microsoft 登录）")}</label><input className="in" data-nu="email" type="email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="e.g. cherry@leverstyle.com" /></div>
          <div className="fl"><label>{t("Username (empty = the email)", "用户名（不填=邮箱）")}</label><input className="in" data-nu="username" value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} placeholder="e.g. cherry" /></div>
          <div className="fl"><label>{t("Display name", "显示名")}</label><input className="in" data-nu="displayName" value={nu.displayName} onChange={(e) => setNu({ ...nu, displayName: e.target.value })} placeholder="e.g. Cherry" /></div>
          {!sso && <div className="fl"><label>{t("Password (local use without Microsoft login)", "密码（未开 Microsoft 登录时本机用）")}</label><input className="in" data-nu="password" type="text" autoComplete="off" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} /></div>}
          <div className="fl"><label>{t("Role", "角色")}</label><select className="in" data-nu="role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value as "admin" | "user" })}>
            <option value="user">{t("User", "普通用户")}</option><option value="admin">{t("Admin", "管理员")}</option></select></div>
          <div className="fl"><label>{t("Can use", "可以使用")}</label>
            {nu.role === "admin" ? <span className="dim" style={{ fontSize: 13 }}>{t("Admins can use every tool", "管理员可以使用所有工具")}</span>
              : <ToolBoxes name="nu" value={nu.tools} onChange={(v) => setNu({ ...nu, tools: v })} />}</div>
        </div>
        <div className="gorow"><button className="btn p" id="nuGo" disabled={(sso ? !nu.email.trim() : !nu.email.trim() && (!nu.username.trim() || !nu.password)) || (!!nu.password && nu.password.length < 6) || (nu.role === "user" && !nu.tools.length)} onClick={create}>{t("Create", "创建")}</button>
          <span className="dim" style={{ fontSize: 12 }}>{t("Colleagues need no account: their first Microsoft login creates one. Create one here only to make someone an admin or give tools in advance.",
            "同事不用预先开通：第一次用 Microsoft 登录会自动建账户。这里只用于提前设管理员或提前开通工具。")}</span></div>
      </div>
      <div className="panel">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>{t("Accounts", "账户")}</h3>
          <span className="dim" id="userCount">{needle ? t(`${shown.length} of ${users.length}`, `${shown.length} / 共 ${users.length} 个`) : t(`${users.length} accounts · newest first`, `共 ${users.length} 个 · 最新创建的在前`)}</span>
          <input className="in" id="userSearch" style={{ maxWidth: 320, marginLeft: "auto" }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search username / email / name", "搜索用户名 / 邮箱 / 显示名")} />
        </div>
        <table className="t" id="userList"><tbody>
          <tr><th>{t("Username", "用户名")}</th><th>{t("Email", "邮箱")}</th><th>{t("Display name", "显示名")}</th><th>{t("Role", "角色")}</th><th>{t("Can use", "可以使用")}</th><th>{t("Status", "状态")}</th><th>{t("Created", "创建")}</th><th>{t("Last login", "最近登录")}</th><th></th></tr>
          {shown.map((u) => (
            <Fragment key={u.id}>
              <tr data-user={u.username}>
                <td className="mono">{u.username}{me?.id === u.id && <span className="dim"> · {t("you", "你")}</span>}</td>
                <td className="mono">{u.email || <span className="dim">—</span>}{u.ssoLinked && <span className="chip ok" style={{ marginLeft: 6 }} title={t("Has logged in with Microsoft", "已用 Microsoft 登录过")}>MS</span>}</td>
                <td>{u.displayName}</td>
                <td>{u.role === "admin" ? t("Admin", "管理员") : t("User", "普通用户")}</td>
                <td data-tools={u.tools.join(",")}>{u.tools.map((k) => t(...TOOL_LABEL[k])).join(" · ")}</td>
                <td>{u.active ? <span className="chip ok">{t("active", "启用")}</span> : <span className="chip">{t("disabled", "已停用")}</span>}</td>
                <td>{when(u.createdAt)}</td><td>{when(u.lastLoginAt)}</td>
                <td><button className="btn sm" data-edit={u.username} onClick={() => { setEdit(edit === u.id ? null : u.id); setEd({ displayName: u.displayName, email: u.email || "", password: "", role: u.role, active: u.active, tools: u.tools }); }}>{t("Edit", "修改")}</button></td>
              </tr>
              {edit === u.id && (
                <tr><td colSpan={9}>
                  <div className="grid4">
                    <div className="fl"><label>{t("Display name", "显示名")}</label><input className="in" data-ed="displayName" value={ed.displayName} onChange={(e) => setEd({ ...ed, displayName: e.target.value })} /></div>
                    <div className="fl"><label>{t("Company email (Microsoft login)", "公司邮箱（Microsoft 登录）")}</label><input className="in" data-ed="email" type="email" value={ed.email} onChange={(e) => setEd({ ...ed, email: e.target.value })} /></div>
                    {!sso && <div className="fl"><label>{t("New password (empty = keep)", "新密码（不填=不改）")}</label><input className="in" data-ed="password" type="text" autoComplete="off" value={ed.password} onChange={(e) => setEd({ ...ed, password: e.target.value })} /></div>}
                    <div className="fl"><label>{t("Role", "角色")}</label><select className="in" data-ed="role" disabled={me?.id === u.id} value={ed.role} onChange={(e) => setEd({ ...ed, role: e.target.value as "admin" | "user" })}>
                      <option value="user">{t("User", "普通用户")}</option><option value="admin">{t("Admin", "管理员")}</option></select></div>
                    <div className="fl"><label>{t("Can log in", "允许登录")}</label><label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input type="checkbox" data-ed="active" disabled={me?.id === u.id} checked={ed.active} onChange={(e) => setEd({ ...ed, active: e.target.checked })} />{ed.active ? t("active", "启用") : t("disabled", "停用")}</label></div>
                    <div className="fl"><label>{t("Can use", "可以使用")}</label>
                      {ed.role === "admin" ? <span className="dim" style={{ fontSize: 13 }}>{t("Admins can use every tool", "管理员可以使用所有工具")}</span>
                        : <ToolBoxes name="ed" value={ed.tools} onChange={(v) => setEd({ ...ed, tools: v })} />}</div>
                  </div>
                  <div className="mb3">
                    {me?.id !== u.id && <button className="btn sm" data-delete={u.username} style={{ marginRight: "auto", color: "#b42318" }} onClick={() => remove(u)}>{t("Delete account", "删除账户")}</button>}
                    <button className="btn sm" onClick={() => setEdit(null)}>{t("Cancel", "取消")}</button>
                    <button className="btn sm p" data-edsave={u.username} disabled={(!!ed.password && ed.password.length < 6) || (ed.role === "user" && !ed.tools.length)} onClick={() => save(u)}>{t("Save", "保存")}</button></div>
                </td></tr>
              )}
            </Fragment>
          ))}
        </tbody></table>
        {needle && !shown.length && <div className="dim">{t("No account matches.", "没有匹配的账户。")}</div>}
      </div>
    </>
  );
}

/* ---------------- fabric price tool (JF 2026-10-08) ---------------- */

function FabricOverview({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const [s, setS] = useState<Stats | null>(null);
  useEffect(() => { api.stats(range).then(setS).catch((e) => useStore.getState().toast(String(e.message || e))); }, [range]);
  if (!s) return <div className="panel dim">{t("Loading…", "加载中…")}</div>;
  const people = s.users.filter((u) => u.tools.includes("fabric"));
  const max = Math.max(1, ...s.daily.map((d) => d.fabric || 0));
  return (
    <>
      <div className="kpis" id="fabKpis">
        <div className="kpi"><b data-kpi="fabricEstimates">{s.fabricEstimates}</b><span>{t("AI fabric estimates", "AI 面料估价（次）")}</span><small>{s.fabricUsers} {t("people used the tool", "人用过")}</small></div>
        <div className="kpi"><b data-kpi="fabricSearches">{s.fabricSearches}</b><span>{t("price-history searches", "查历史价（次）")}</span><small>{people.length} {t("accounts can use it", "个账户可用")}</small></div>
      </div>
      <div className="panel">
        <h3>{t("Per person", "按人统计")}</h3>
        <table className="t" id="fabPerUser"><tbody>
          <tr><th>{t("Account", "账户")}</th><th className="n">{t("AI estimates", "AI 估价")}</th><th className="n">{t("Searches", "查历史价")}</th><th>{t("Last active", "最近活动")}</th><th>{t("Last login", "最近登录")}</th></tr>
          {people.map((u) => (
            <tr key={u.id} style={u.active ? undefined : { opacity: 0.5 }}>
              <td>{who(u)} <span className="dim mono">{u.username}</span>{u.role === "admin" && <span className="chip" style={{ marginLeft: 6 }}>admin</span>}</td>
              <td className="n">{u.fabricEstimates}</td><td className="n">{u.fabricSearches}</td><td>{when(u.lastActive)}</td><td>{when(u.lastLoginAt)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <div className="panel">
        <h3>{t("Per day", "按天")}</h3>
        {!s.daily.some((d) => d.fabric) && <div className="dim">{t("No fabric queries in this period.", "这段时间没有面料询价。")}</div>}
        <table className="t daily"><tbody>
          {s.daily.filter((d) => d.fabric).map((d) => (
            <tr key={d.d}><td className="mono">{d.d}</td>
              <td style={{ width: "70%" }}><div className="bar p" style={{ width: (d.fabric / max) * 100 + "%" }} /><span className="dim">{d.fabric} {t("queries", "次查询")}</span></td></tr>
          ))}
        </tbody></table>
      </div>
    </>
  );
}

/** "88% Polyester 12% Spandex · 针织 · 180 GSM · 150 cm" */
function specLine(sp: Record<string, unknown> | undefined): string {
  if (!sp) return "—";
  const c = sp.construction === "knit" ? "针织" : sp.construction === "woven" ? "梭织" : "";
  const w = sp.width ?? sp.width_cm;
  return [sp.composition, c, sp.gsm ? sp.gsm + " GSM" : "", w ? w + " cm" : "", sp.finish, sp.q ?? sp.notes].filter(Boolean).join(" · ") || "—";
}

function FabricRecords({ range, users }: { range: { from: string; to: string }; users: AdminUser[] }) {
  const t = useT();
  const [kind, setKind] = useState("");
  const L = useList<FabricRecord>((r) => api.adminFabric({ ...r, kind }), { from: range.from, to: range.to, kind } as { from?: string; to?: string });
  const [open, setOpen] = useState<FabricRecord | null>(null);
  const per = (x: FabricRecord) => (String(x.detail.spec?.unit || "m") === "kg" ? "/kg" : "/m");
  return (
    <div className="panel">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Filters users={users} user={L.user} setUser={L.setUser} q={L.q} setQ={L.setQ} placeholder={t("Composition / code / keyword", "成分 / 料号 / 关键词")} />
        <select className="in" style={{ width: "auto" }} id="fabKind" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">{t("All queries", "全部")}</option><option value="fabric_estimate">{t("AI estimates", "AI 估价")}</option><option value="fabric_search">{t("Searches", "查历史价")}</option></select>
      </div>
      <table className="t" id="fabRecList"><tbody>
        <tr><th>{t("Time", "时间")}</th><th>{t("Who", "谁")}</th><th>{t("Type", "类型")}</th><th>{t("Spec", "规格")}</th><th className="n">{t("Result", "结果")}</th></tr>
        {L.data?.items.map((x) => {
          const e = x.detail.estimate, st = x.detail.stats;
          const result = e ? `$${f2(e.low)} – $${f2(e.high)}${per(x)}`
            : st ? `${t("median", "中位")} $${f2(st.med)}${per(x)} · ${x.detail.total ?? st.n}`
            : x.detail.total != null ? `${x.detail.total} ${t("found", "条")}` : "—";
          return (
            <tr className="rec" key={x.id} onClick={() => setOpen(x)}>
              <td className="mono">{when(x.at)}</td><td>{who(x)}</td>
              <td>{x.kind === "fabric_estimate" ? <span className="chip ok">{t("AI estimate", "AI 估价")}</span> : <span className="chip">{t("search", "查历史价")}</span>}</td>
              <td>{specLine(x.detail.spec)}</td><td className="n">{result}</td>
            </tr>
          );
        })}
      </tbody></table>
      {L.data && !L.data.items.length && <div className="dim">{t("No fabric queries yet.", "还没有面料询价记录。")}</div>}
      {L.data && <Pager total={L.data.total} offset={L.offset} setOffset={L.setOffset} />}
      {open && (
        <Modal onClose={() => setOpen(null)} wide>
          <h3>{open.kind === "fabric_estimate" ? t("AI estimate", "AI 估价") : t("Price-history search", "查历史价")} <span className="dim" style={{ fontWeight: 400, fontSize: 13 }}>{who(open)} · {when(open.at)}</span></h3>
          <div className="kv" id="fabRecDetail">
            <div>{t("Spec", "规格")}</div><div>{specLine(open.detail.spec)}</div>
            {open.detail.estimate && <>
              <div>{t("Estimate", "估价")}</div><div>${f2(open.detail.estimate.mid)} （${f2(open.detail.estimate.low)} – ${f2(open.detail.estimate.high)}）{per(open)} · {t("confidence", "可信度")} {open.detail.estimate.confidence}</div>
              <div>{t("Basis", "依据")}</div><div>{open.detail.estimate.basis || "—"}</div>
              <div>{t("References", "参考面料")}</div><div className="mono">{open.detail.estimate.refs.join(", ") || "—"}</div>
            </>}
            {open.detail.stats && <><div>{t("Similar fabrics", "相似面料")}</div><div>{open.detail.stats.n} · P25 ${f2(open.detail.stats.p25)} · {t("median", "中位数")} ${f2(open.detail.stats.med)} · P75 ${f2(open.detail.stats.p75)}</div></>}
          </div>
          <pre className="raw">{JSON.stringify(open.detail, null, 2)}</pre>
          <div className="mb"><button className="btn" onClick={() => setOpen(null)}>{t("Close", "关闭")}</button></div>
        </Modal>
      )}
    </div>
  );
}
